import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Heading,
  HStack,
  Text,
  useToast,
  VStack,
  Select,
  Textarea,
  AbsoluteCenter,
  CircularProgress,
  Box,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";
import { useForm, SubmitHandler } from "react-hook-form";
import { createRegistration, updateRegistration } from "../../api/registration";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { LabeledInput } from "../molecules/LabeledInput";
import { PhotoInput } from "../molecules/PhotoInput";
import type { Discount } from "../../types/global";

// Types
interface DiscountFormData {
  image?: File | string;
  mode: 'local' | 'online';
  type: 'discount' | 'buy_and_gain' | 'treat';
  discount?: number;
  name: string;
  rules_other?: string;
  company?: number;
  stores?: number[];
  active?: boolean;
  rules?: string;
}

interface DiscountFormProps {
  onNext: (discount?: any) => void;
  companyId: number;
  storeId: number;
  initialData?: Partial<Discount>;
  onReturn?: () => void;
  onClose: () => void;
  nextText?: string;
  updateFunc?: (step: number, data: any) => Promise<any>;
  createFunc?: (step: number, data: any) => Promise<any>;
  refetch?: () => void;
}

export const DiscountForm: React.FC<DiscountFormProps> = ({
  onNext,
  companyId,
  storeId,
  initialData = {},
  onReturn,
  onClose,
  nextText = "Salvar Desconto",
  updateFunc = updateRegistration,
  createFunc = createRegistration,
  refetch,
}) => {
  const toast = useToast();
  const { register, handleSubmit, setValue, watch, getValues, formState: { errors } } = useForm<DiscountFormData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [type, setType] = useState<string>(getValues("type") || "");
  const [mode, setMode] = useState<string>(getValues("mode") || "");
  const [allowed, setAllowed] = useState<boolean>(false);
  const [others, setOthers] = useState<boolean>(false);
  const [rules, setRules] = useState<string[]>([]);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState<boolean>(true);
  const [image, setImage] = useState<string | undefined>(initialData?.image);
  const [formKey, setFormKey] = useState<number>(0); // Force re-render for validation

  const handleImageSizeError = (fileName: string, size: number) => {
    toast({
      title: "Arquivo muito grande",
      description: `A imagem "${fileName}" excede o limite de 2MB. Tamanho atual: ${(size / 1024 / 1024).toFixed(2)}MB`,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  };

  const validateForm = (): boolean => {
    // Check if image is present
    if (!image && !initialData?.image) {
      toast({
        title: "Imagem obrigatória",
        description: "Por favor, adicione uma imagem para o desconto.",
        status: "error",
      });
      return false;
    }

    // Check required fields
    if (!mode) {
      toast({
        title: "Campo obrigatório",
        description: "Selecione o modo de desconto.",
        status: "error",
      });
      return false;
    }

    if (!type) {
      toast({
        title: "Campo obrigatório", 
        description: "Selecione o tipo do desconto.",
        status: "error",
      });
      return false;
    }

    if (type === "discount" && !getValues("discount")) {
      toast({
        title: "Campo obrigatório",
        description: "Informe a porcentagem do desconto.",
        status: "error",
      });
      return false;
    }

    if (!getValues("name")) {
      toast({
        title: "Campo obrigatório",
        description: "Digite o título do desconto.",
        status: "error",
      });
      return false;
    }

    if (!allowed) {
      toast({
        title: "Confirmação necessária",
        description: "Você deve aceitar os termos antes de prosseguir.",
        status: "warning",
      });
      return false;
    }

    return true;
  };

  const onSubmit: SubmitHandler<DiscountFormData> = async (data) => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Prepare data
      const formData = {
        ...data,
        company: companyId,
        stores: [storeId],
        active: true,
        rules: rules.join("\n") + (data.rules_other && others ? "\n\n" + data.rules_other : ""),
      };

      const func = initialData?.id ? updateFunc : createFunc;
      const discount = await func!(2, formData);

      toast({
        title: "Sucesso!",
        description: initialData?.id ? "Desconto atualizado com sucesso!" : "Desconto criado com sucesso!",
        status: "success",
      });

      if (refetch) {
        refetch();
      }

      onNext(discount);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Erro ao salvar",
        description: "Algo deu errado, verifique as informações e tente novamente.",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if form is valid for submit button state
  const isFormValid = (): boolean => {
    const hasImage = !!(image || initialData?.image);
    const hasMode = !!mode;
    const hasType = !!type;
    const hasDiscount = type !== "discount" || !!getValues("discount");
    const hasName = !!getValues("name");
    const hasAcceptedTerms = allowed;

    return hasImage && hasMode && hasType && hasDiscount && hasName && hasAcceptedTerms;
  };

  // Watch form changes
  
  useEffect(() => {
    const subscription = watch((data, { name }) => {
      if (name === "type") {
        const _type = data.type;
        setType(_type || "");

        if (initialData?.name) return;

        if (_type === "buy_and_gain") setValue("name", "Compre e Ganhe!");
        else if (_type === "treat") setValue("name", "Mimo");
      } else if (name === "discount") {
        if (initialData?.name) return;
        setValue("name", `${data.discount}% de desconto na conta!`);
      } else if (name === "mode") {
        setMode(data.mode || "");
      } else if (name === "image") {
        setImage(data.image as string);
      }
      
      // Force re-render to update validation status
      setFormKey(prev => prev + 1);
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, initialData?.name]);

  // Initialize form with initial data
  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) {
      setIsLoadingInitialData(false);
      return;
    }

    Object.entries(initialData).forEach(([key, value]) => {
      if (key === "rules" && value && typeof value === "string") {
        const split = value.split("\r\n\r\n");
        setValue("rules", split[0]);
        setRules(split[0].split("\r\n"));

        if (split.length > 1) {
          setValue("rules_other", split.slice(1).join("\r\n\r\n"));
          setOthers(true);
        }
      } else {
        setValue(key as keyof DiscountFormData, value);
      }
    });

    // Set initial states
    setType(initialData.type || "");
    setMode(initialData.mode || "");
    setImage(initialData.image);

    setIsLoadingInitialData(false);
  }, [initialData, setValue]);

  // Update validation when checkbox or image changes
  useEffect(() => {
    setFormKey(prev => prev + 1);
  }, [allowed, image]);

  if (isLoadingInitialData) {
    return (
      <AbsoluteCenter>
        <CircularProgress isIndeterminate color="green.500" />
      </AbsoluteCenter>
    );
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} width="100%">
      <Alert status="info" borderRadius="md" mb={6}>
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Os campos abaixo são referentes ao desconto que será providenciado. Todos os descontos passarão por análise da equipe Partiu antes de serem publicados.
        </AlertDescription>
      </Alert>

      <VStack spacing={6} align="stretch">
        {/* Image Upload Section */}
        <Box>
          <Heading size="sm" mb={3} color="gray.700">
            Imagem do Desconto <Text as="span" color="red.500">*</Text>
          </Heading>
          <PhotoInput
            {...register("image")}
            value={image}
            maxSize={2}
            onFileSizeError={handleImageSizeError}
            accept="image/png,image/jpg,image/jpeg"
          />
        </Box>

        {/* Basic Information Section */}
        <Box>
          <Heading size="sm" mb={4} color="gray.700">
            Informações Básicas
          </Heading>
          <VStack spacing={4} align="stretch">
            <LabeledInput
              Component={Select}
              {...register("mode", { required: "Selecione o modo de desconto" })}
              value={mode}
              label="Modo de desconto *"
            >
              <option value="">Selecione...</option>
              <option value="local">Local (Presencial)</option>
              <option value="online">Online (Delivery/Retirada)</option>
            </LabeledInput>

            <LabeledInput
              {...register("type", { required: "Selecione o tipo do desconto" })}
              Component={Select}
              label="Tipo do Desconto *"
              value={type}
            >
              <option value="">Selecione...</option>
              <option value="discount">Desconto Percentual</option>
              <option value="buy_and_gain">Compre e Ganhe</option>
              <option value="treat">Mimo (Brinde)</option>
            </LabeledInput>

            {type === "discount" && (
              <LabeledInput
                type="number"
                min={1}
                max={100}
                {...register("discount", { 
                  required: "Informe a porcentagem do desconto",
                  min: { value: 1, message: "Mínimo 1%" },
                  max: { value: 100, message: "Máximo 100%" }
                })}
                label="Porcentagem do desconto *"
                placeholder="Ex: 10"
              />
            )}

            <LabeledInput
              {...register("name", { required: "Digite o título do desconto" })}
              label="Título do desconto *"
              placeholder="Ex: 10% de desconto na conta, Compre 1 leve 2, Sobremesa grátis"
            />
          </VStack>
        </Box>

        {/* Rules Section */}
        <Box>
          <Heading size="sm" mb={4} color="gray.700">
            Regulamento do Desconto
          </Heading>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.600">
              Selecione as regras que se aplicam ao seu desconto:
            </Text>
            
            <CheckboxGroup
              value={rules}
              colorScheme="green"
              onChange={(values) => setRules(values as string[])}
            >
              <VStack align="stretch" spacing={3}>
                <Checkbox value="Não cumulativa com outras promoções.">
                  <Text fontSize="sm">Não cumulativa com outras promoções</Text>
                </Checkbox>
                <Checkbox value="Exceto Delivery.">
                  <Text fontSize="sm">Exceto Delivery</Text>
                </Checkbox>
                <Checkbox value="Promoção válida somente para essa unidade.">
                  <Text fontSize="sm">Promoção válida somente para essa unidade</Text>
                </Checkbox>
                <Checkbox value="Sujeito a disponibilidade do produto e lotação do estabelecimento.">
                  <Text fontSize="sm">
                    Sujeito a disponibilidade do produto e lotação do estabelecimento
                  </Text>
                </Checkbox>
              </VStack>
            </CheckboxGroup>

            <Box>
              <Checkbox
                colorScheme="green"
                isChecked={others}
                onChange={(e) => setOthers(e.target.checked)}
                mb={3}
              >
                <Text fontSize="sm">Outras regras específicas</Text>
              </Checkbox>
              
              {others && (
                <Textarea
                  {...register("rules_other")}
                  placeholder="Digite outras regras específicas do seu desconto..."
                  size="sm"
                  resize="vertical"
                  minH="80px"
                />
              )}
            </Box>
          </VStack>
        </Box>

        <Divider />



        {/* Terms Acceptance */}
        <Box>
          <Checkbox
            colorScheme="green"
            isChecked={allowed}
            onChange={(e) => setAllowed(e.target.checked)}
            alignItems="flex-start"
          >
            <Text fontSize="sm" fontWeight="bold" color="gray.700">
              Entendo que o desconto só será disponibilizado quando aprovado pela equipe Partiu <Text as="span" color="red.500">*</Text>
            </Text>
          </Checkbox>
        </Box>

        {/* Action Buttons */}
        <HStack justify="space-between" pt={4}>
          <HStack>
            <Button
              variant="ghost"
              onClick={onClose}
              isDisabled={loading}
            >
              Cancelar
            </Button>
            {onReturn && (
              <Button
                variant="ghost"
                onClick={onReturn}
                leftIcon={<FontAwesomeIcon icon={faChevronLeft} />}
                isDisabled={loading}
              >
                Voltar
              </Button>
            )}
          </HStack>
          
          <Button
            type="submit"
            colorScheme="green"
            isLoading={loading}
            loadingText="Salvando..."
            isDisabled={!isFormValid() || loading}
            rightIcon={<FontAwesomeIcon icon={faCheck} />}
          >
            {nextText}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};
